import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, CircularProgress, Grid, MenuItem, IconButton,
  Tooltip, InputAdornment, Avatar, Divider, Dialog, DialogTitle, DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search, FileDownload, FilterList, Person, DirectionsCar,
  Timeline, Map, Speed, Schedule,
  TrendingUp, Refresh, CalendarToday, Close, ArrowBack,
  LocationOn, Map as MapIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { InvokeApi, apiList } from '../services/ApiService';
import { Config } from '../config/Config';
import dayjs from 'dayjs';

// Fix Leaflet icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const MAP_LAYERS = {
  Street: { label: 'Street', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '© OpenStreetMap contributors', maxZoom: 19 },
  Satellite: { label: 'Satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '© Esri', maxZoom: 19 },
  Dark: { label: 'Dark', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', attribution: '© CartoDB', maxZoom: 19 },
};

const StatCard = ({ icon, label, value, color, subtitle }) => (
  <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
    <CardContent sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Avatar sx={{ width: 48, height: 48, bgcolor: `${color}20`, color }}>
          {icon}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography fontSize={11} color="text.secondary" fontWeight={600} textTransform="uppercase" mb={0.5}>
            {label}
          </Typography>
          <Typography variant="h5" fontWeight={800} color={color} mb={0.3}>
            {value}
          </Typography>
          {subtitle && (
            <Typography fontSize={10} color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const FleetMovementReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Filters
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [search, setSearch] = useState('');
  const [assignedFilter, setAssignedFilter] = useState(''); // '', 'assigned', 'unassigned'

  // Path Dialog state
  const [pathDialog, setPathDialog] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [pathLoading, setPathLoading] = useState(false);
  const [pathPoints, setPathPoints] = useState([]);
  const [pathData, setPathData] = useState(null);
  const [pathMapLayer, setPathMapLayer] = useState('Street');
  const [pathDate, setPathDate] = useState(null);
  const [pathError, setPathError] = useState(null);
  const pathMapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const tileLayerRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, date]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await InvokeApi(Config.base_url + apiList.fleetMovementReport, {
        date,
        page: page + 1,
        per_page: rowsPerPage,
        search: search.trim(),
        assigned_filter: assignedFilter,
      });
      
      if (res?.code === '200') {
        setData(res.vehicles || []);
        setStats(res.stats || {});
        setTotal(res.total || 0);
      } else {
        console.error('Fleet report API error:', res?.message);
        setData([]);
        setStats({});
        setTotal(0);
      }
    } catch (err) {
      console.error('Fleet report fetch error:', err);
      setData([]);
      setStats({});
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchData();
  };

  const handleReset = () => {
    setSearch('');
    setAssignedFilter('');
    setDate(new Date().toISOString().split('T')[0]);
    setPage(0);
    setTimeout(fetchData, 100);
  };

  const exportToCSV = () => {
    const headers = ['Vehicle', 'Model', 'Status', 'Assigned To', 'Phone', 'Distance (km)', 'Avg Speed', 'Max Speed', 'Idle Time (min)', 'Movement Status'];
    const rows = data.map(row => [
      row.vehicle_number,
      row.model,
      row.status,
      row.assigned_user || 'Unassigned',
      row.assigned_phone || '',
      row.distance_km || 0,
      row.avg_speed_kmh || 0,
      row.max_speed_kmh || 0,
      row.idle_duration_minutes || 0,
      row.movement_status || 'No Movement',
    ]);

    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fleet-movement-report-${date}.csv`;
    a.click();
  };

  const openPathDialog = async (vehicle) => {
    setSelectedVehicle(vehicle);
    setPathDialog(true);
    setPathDate(date); // Set the current selected date
    setPathLoading(true);
    setPathPoints([]);
    setPathData(null);
    setPathError(null);
    
    try {
      // Use the SAME API endpoint as VehicleDetail for consistency
      const res = await InvokeApi(Config.base_url + apiList.getVehiclePathHistory, {
        vehicle_id: vehicle.id,
        date: date,
      });
      
      if (res?.code === '200' && Array.isArray(res.points) && res.points.length > 0) {
        setPathData(res);
        // Normalize GPS points for Leaflet: backend returns { latitude, longitude, speed, timestamp }
        setPathPoints(res.points.map(p => ({
          lat: parseFloat(p.latitude),
          lng: parseFloat(p.longitude),
          speed: parseFloat(p.speed || 0),
          timestamp: p.timestamp,
        })));
        setPathError(null);
      } else {
        console.error('Path API error:', res?.message || 'No data');
        setPathPoints([]);
        setPathData(null);
        setPathError(res?.message || 'No GPS path data available for this date.');
      }
    } catch (err) {
      console.error('Failed to fetch vehicle path:', err);
      setPathPoints([]);
      setPathData(null);
      setPathError('Failed to load path: ' + err.message);
    } finally {
      setPathLoading(false);
    }
  };

  const closePathDialog = () => {
    if (leafletMapRef.current) { 
      leafletMapRef.current.remove(); 
      leafletMapRef.current = null; 
      tileLayerRef.current = null; 
    }
    setPathDialog(false);
    setSelectedVehicle(null);
    setPathPoints([]);
    setPathData(null);
    setPathDate(null);
    setPathError(null);
  };

  // ── Leaflet: init map when points arrive ──────────────────────────────────────
  useEffect(() => {
    if (!pathDialog || pathLoading || pathPoints.length === 0 || !pathMapRef.current) return;
    if (leafletMapRef.current) { 
      leafletMapRef.current.remove(); 
      leafletMapRef.current = null; 
      tileLayerRef.current = null; 
    }

    const initMap = () => {
      const L = window.L;
      if (!L) return;
      const coords = pathPoints.map(p => [parseFloat(p.lat), parseFloat(p.lng)]);
      const map = L.map(pathMapRef.current).fitBounds(coords);
      leafletMapRef.current = map;
      const layer = MAP_LAYERS[pathMapLayer] || MAP_LAYERS.Street;
      tileLayerRef.current = L.tileLayer(layer.url, { attribution: layer.attribution, maxZoom: layer.maxZoom }).addTo(map);
      L.polyline(coords, { color: '#0277bd', weight: 4, opacity: 0.85 }).addTo(map);
      // Start marker
      L.marker(coords[0], { 
        icon: L.divIcon({ 
          html: '<div style="background:#2e7d32;width:14px;height:14px;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4)"></div>', 
          iconSize:[14,14], 
          iconAnchor:[7,7], 
          className:'' 
        }) 
      })
        .addTo(map).bindPopup(`<b>Start</b><br>${dayjs(String(pathPoints[0].timestamp).replace(' ','T')).format('hh:mm:ss A')}`).openPopup();
      // End marker
      const last = pathPoints[pathPoints.length - 1];
      L.marker(coords[coords.length - 1], { 
        icon: L.divIcon({ 
          html: '<div style="background:#c62828;width:14px;height:14px;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4)"></div>', 
          iconSize:[14,14], 
          iconAnchor:[7,7], 
          className:'' 
        }) 
      })
        .addTo(map).bindPopup(`<b>End</b><br>${dayjs(String(last.timestamp).replace(' ','T')).format('hh:mm:ss A')}`);
    };

    if (!window.L) {
      if (!document.getElementById('leaflet-css')) {
        const link = document.createElement('link'); 
        link.id='leaflet-css'; 
        link.rel='stylesheet';
        link.href='https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'; 
        document.head.appendChild(link);
      }
      if (!document.getElementById('leaflet-js')) {
        const script = document.createElement('script'); 
        script.id='leaflet-js';
        script.src='https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'; 
        script.onload=initMap; 
        document.head.appendChild(script);
      }
    } else { 
      initMap(); 
    }

    return () => { 
      if (leafletMapRef.current) { 
        leafletMapRef.current.remove(); 
        leafletMapRef.current = null; 
        tileLayerRef.current = null; 
      } 
    };
  }, [pathDialog, pathLoading, pathPoints]);

  // ── Swap tile layer without re-init ──────────────────────────────────────────
  useEffect(() => {
    const L = window.L;
    if (!L || !leafletMapRef.current || !tileLayerRef.current) return;
    const layer = MAP_LAYERS[pathMapLayer] || MAP_LAYERS.Street;
    leafletMapRef.current.removeLayer(tileLayerRef.current);
    tileLayerRef.current = L.tileLayer(layer.url, { attribution: layer.attribution, maxZoom: layer.maxZoom }).addTo(leafletMapRef.current);
  }, [pathMapLayer]);

  const getMovementColor = (status) => {
    switch (status) {
      case 'High Usage': return { bg: '#e8f5e9', color: '#2e7d32' };
      case 'Normal': return { bg: '#e3f2fd', color: '#0277bd' };
      case 'Minimal': return { bg: '#fff3e0', color: '#e65100' };
      default: return { bg: '#f5f5f5', color: '#757575' };
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">
            Fleet Movement Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Daily vehicle tracking and distance analytics
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            sx={{ borderColor: '#1a237e', color: '#1a237e', fontWeight: 600 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<FileDownload />}
            onClick={exportToCSV}
            disabled={data.length === 0}
            sx={{ background: 'linear-gradient(135deg,#2e7d32,#388e3c)', fontWeight: 600 }}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Stats Dashboard */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<DirectionsCar />}
              label="Vehicles Tracked"
              value={stats.total_vehicles_tracked || 0}
              color="#1a237e"
              subtitle="With GPS data"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<TrendingUp />}
              label="Total Distance"
              value={`${parseFloat(stats.total_distance || 0).toFixed(1)} km`}
              color="#2e7d32"
              subtitle="All vehicles combined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<Speed />}
              label="Avg Distance"
              value={`${parseFloat(stats.avg_distance || 0).toFixed(1)} km`}
              color="#0277bd"
              subtitle="Per vehicle"
            />
          </Grid>
          {/* <Grid item xs={12} sm={6} md={2.4}>
            <StatCard
              icon={<Map />}
              label="Total Trips"
              value={stats.total_trips || 0}
              color="#6a1b9a"
              subtitle="Across all vehicles"
            />
          </Grid> */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<Schedule />}
              label="Idle Time"
              value={`${Math.floor((stats.total_idle_minutes || 0) / 60)}h ${(stats.total_idle_minutes || 0) % 60}m`}
              color="#e65100"
              subtitle="Total fleet idle"
            />
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3, borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <FilterList sx={{ color: '#1a237e' }} />
            <Typography variant="h6" fontWeight={700} color="#1a237e">Filters</Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Select Date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                placeholder="Vehicle, Model, User"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                select
                size="small"
                label="Assignment"
                value={assignedFilter}
                onChange={(e) => setAssignedFilter(e.target.value)}
              >
                <MenuItem value="">All Vehicles</MenuItem>
                <MenuItem value="assigned">Assigned Only</MenuItem>
                <MenuItem value="unassigned">Unassigned Only</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSearch}
                sx={{ height: 40, background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontWeight: 600 }}
              >
                Search
              </Button>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleReset}
                sx={{ height: 40, borderColor: '#1a237e', color: '#1a237e', fontWeight: 600 }}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          Showing {data.length} of {total} vehicles for {new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
        </Typography>
      </Box>

      {/* Table */}
      <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ background: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Vehicle</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Assigned To</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a237e', textAlign: 'right' }}>Distance</TableCell>
                {/* <TableCell sx={{ fontWeight: 700, color: '#1a237e', textAlign: 'center' }}>Trips</TableCell> */}
                <TableCell sx={{ fontWeight: 700, color: '#1a237e', textAlign: 'center' }}>Avg Speed</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a237e', textAlign: 'center' }}>Max Speed</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a237e', textAlign: 'center' }}>Idle Time</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Movement</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <CircularProgress sx={{ color: '#1a237e' }} />
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <Typography color="text.secondary">No vehicles found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row) => {
                  const movementStyle = getMovementColor(row.movement_status);
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: '#e8eaf6', color: '#1a237e', fontSize: 12, fontWeight: 700 }}>
                            <DirectionsCar fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography fontSize={13} fontWeight={700}>{row.vehicle_number}</Typography>
                            <Typography fontSize={11} color="text.secondary">{row.model}</Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell>
                        {row.assigned_user ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 28, height: 28, bgcolor: '#e3f2fd', color: '#1565c0', fontSize: 11, fontWeight: 700 }}>
                              {row.assigned_user[0].toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography fontSize={12} fontWeight={600}>
                                {row.assigned_user}
                                {row.user_status == 0 && (
                                  <Chip label="Inactive" size="small" sx={{ ml: 0.5, height: 16, fontSize: 9, fontWeight: 700, bgcolor: '#ffebee', color: '#c62828' }} />
                                )}
                              </Typography>
                              <Typography fontSize={10} color="text.secondary">{row.assigned_phone}</Typography>
                            </Box>
                          </Box>
                        ) : (
                          <Chip label="Unassigned" size="small" sx={{ bgcolor: '#f5f5f5', color: '#757575', fontWeight: 600, fontSize: 11 }} />
                        )}
                      </TableCell>

                      <TableCell align="right">
                        <Typography fontSize={14} fontWeight={800} color="#2e7d32">
                          {parseFloat(row.distance_km || 0).toFixed(1)} km
                        </Typography>
                      </TableCell>

                      {/* <TableCell align="center">
                        <Typography fontSize={13} fontWeight={700}>
                          {row.trips || 0}
                        </Typography>
                      </TableCell> */}

                      <TableCell align="center">
                        <Typography fontSize={13} fontWeight={600} color="text.secondary">
                          {parseFloat(row.avg_speed_kmh || 0).toFixed(0)} km/h
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography fontSize={13} fontWeight={700} color={row.max_speed_kmh > 60 ? '#e65100' : 'text.primary'}>
                          {parseFloat(row.max_speed_kmh || 0).toFixed(0)} km/h
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography fontSize={12} fontWeight={600} color="text.secondary">
                          {row.idle_duration_minutes ? `${Math.floor(row.idle_duration_minutes / 60)}h ${row.idle_duration_minutes % 60}m` : '—'}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={row.movement_status || 'No Movement'}
                          size="small"
                          sx={{
                            bgcolor: movementStyle.bg,
                            color: movementStyle.color,
                            fontWeight: 700,
                            fontSize: 10,
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="View Path">
                            <IconButton
                              size="small"
                              onClick={() => openPathDialog(row)}
                              sx={{ bgcolor: '#e8f5e9', '&:hover': { bgcolor: '#c8e6c9' } }}
                            >
                              <Timeline sx={{ fontSize: 16, color: '#2e7d32' }} />
                            </IconButton>
                          </Tooltip>
                          {row.assigned_user && (
                            <Tooltip title="View User">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/user/${row.user_id}`)}
                                sx={{ bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' } }}
                              >
                                <Person sx={{ fontSize: 16, color: '#1565c0' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Vehicle Details">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/vehicle/${row.id}`)}
                              sx={{ bgcolor: '#e8eaf6', '&:hover': { bgcolor: '#c5cae9' } }}
                            >
                              <DirectionsCar sx={{ fontSize: 16, color: '#1a237e' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </Card>

      {/* Path Dialog */}
      <Dialog 
        open={pathDialog} 
        onClose={closePathDialog} 
        maxWidth="md" 
        fullWidth 
        PaperProps={{ sx: { borderRadius: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#0277bd', borderBottom: '1px solid #f0f0f0', pb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={closePathDialog} sx={{ mr: 0.5 }}>
              <ArrowBack fontSize="small" />
            </IconButton>
            <MapIcon sx={{ color: '#0277bd' }} />
            <Box>
              <Typography fontWeight={700} color="#0277bd">
                GPS Track Path — {selectedVehicle?.vehicle_number}
              </Typography>
              <Typography fontSize={12} color="text.secondary">
                {pathDate ? dayjs(pathDate).format('dddd, DD MMMM YYYY') : ''}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {pathLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, gap: 2 }}>
              <CircularProgress sx={{ color: '#0277bd' }} />
              <Typography color="text.secondary" fontSize={13}>Loading GPS path data…</Typography>
            </Box>
          ) : pathError ? (
            <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
              <LocationOn sx={{ fontSize: 56, color: '#e8eaf6', mb: 1.5 }} />
              <Typography color="text.secondary" fontWeight={600} fontSize={15}>No path data available</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{pathError}</Typography>
            </Box>
          ) : pathPoints.length > 0 ? (
            <Box>
              {/* Stats + layer switcher bar */}
              <Box sx={{ display: 'flex', gap: 2, px: 2.5, py: 1.5, background: '#e3f2fd', borderBottom: '1px solid #bbdefb', flexWrap: 'wrap', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: '#2e7d32', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
                  <Typography fontSize={12} fontWeight={600} color="#2e7d32">
                    Start: {dayjs(String(pathPoints[0]?.timestamp).replace(' ', 'T')).format('hh:mm:ss A')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: '#c62828', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
                  <Typography fontSize={12} fontWeight={600} color="#c62828">
                    End: {dayjs(String(pathPoints[pathPoints.length - 1]?.timestamp).replace(' ', 'T')).format('hh:mm:ss A')}
                  </Typography>
                </Box>
                {/* Map layer switcher */}
                <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                  {Object.entries(MAP_LAYERS).map(([key, val]) => (
                    <Button 
                      key={key} 
                      size="small"
                      variant={pathMapLayer === key ? 'contained' : 'outlined'}
                      onClick={() => setPathMapLayer(key)}
                      sx={{ 
                        fontSize: 10, 
                        fontWeight: 700, 
                        px: 1, 
                        py: 0.3, 
                        minWidth: 0, 
                        borderRadius: 1.5,
                        ...(pathMapLayer === key
                          ? { background: '#0277bd', color: '#fff', borderColor: '#0277bd' }
                          : { color: '#0277bd', borderColor: '#90caf9', background: '#fff' }
                        )
                      }}
                    >
                      {val.label}
                    </Button>
                  ))}
                </Box>
              </Box>
              {/* Leaflet map container */}
              <Box ref={pathMapRef} sx={{ width: '100%', height: 460 }} />
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={closePathDialog} variant="outlined" sx={{ borderRadius: 2 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FleetMovementReport;
